/**
 * useMenuRoles — derives menu role flags directly, replacing the Angular
 * MenuRolesUpdated / requestMenuRoles event dependency.
 *
 * Role flags split into two tiers:
 *
 * 1. SYNC (from sessionStorage on mount — no API needed):
 *    admRole, scoreCardRole, pemRole
 *    → derived from currentUser/loggedUser UserPersonas
 *
 * 2. ASYNC (require API calls — mirrors what Angular did before dispatching):
 *    pcorData    → POST patient-care-metrics/
 *                  (Angular: PcorService.getPCORMedicareData → checkPcorData)
 *                  Active when data.Data.ReportingPeriod exists
 *    admData     → GET adm-state-details/{providerSysKey}
 *                  Active when the response carries at least one state/LOB row
 *    affData     → POST standard-search/topfilter with journey payload
 *                  { RequestedJourney:'provTinListFilter', ProvTinListFilter:{ ProviderSysKey|Tin } }
 *                  Active when data.Data has rows
 *    cesfData    → POST extended-tins/top-filter
 *                  (Angular: ExemptionSharedService.checkCESFData — only if affData is false)
 *                  Active when data.Data.length > 0
 *    pocaEnabled → VIM API is FULLY COMMENTED OUT in Angular → always false
 *
 * Role mapping (from interactions-uhci-webui2/src/environments/roles.ts):
 *   admRole / scoreCardRole → General_and_Network_Management | UHCI_Project | UHCI_Admin | GeneralPlus_PEM
 *   pemRole                 → GeneralPlus_ProviderExperienceMeasurement | UHCI_Admin | UHCI_Project
 */

import { useState, useEffect } from 'react';
import { environment } from '@src/config';
import httpService from '@src/utility/httpService';

export interface MenuRoles {
  admRole: boolean;
  scoreCardRole: boolean;
  pemRole: boolean;
  pcorData: boolean;
  admData: boolean;
  affData: boolean;
  cesfData: boolean;
  pocaEnabled: boolean;
}

const SCORE_CARD_ADM_ROLES = new Set([
  'General_and_Network_Management',
  'UHCI_Project',
  'UHCI_Admin',
  'GeneralPlus_ProviderExperienceMeasurement',
]);

const PEM_ROLES = new Set([
  'GeneralPlus_ProviderExperienceMeasurement',
  'UHCI_Admin',
  'UHCI_Project',
]);

const DEFAULT_ROLES: MenuRoles = {
  admRole: false,
  scoreCardRole: false,
  pemRole: false,
  pcorData: false,
  admData: false,
  affData: false,
  cesfData: false,
  pocaEnabled: false,
};

const menuRolesHttp = new httpService();
menuRolesHttp.setBaseURL(environment.apiProxyUrl);

// -- Sync: derive role flags from UserPersonas
function deriveSyncRoles(): Pick<MenuRoles, 'admRole' | 'scoreCardRole' | 'pemRole'> {
  try {
    const userStr =
      sessionStorage.getItem('loggedUser') ||
      sessionStorage.getItem('currentUser');

    if (!userStr) return { admRole: false, scoreCardRole: false, pemRole: false };

    const parsed = JSON.parse(userStr);
    const user = Array.isArray(parsed) ? parsed[0] : parsed;
    const userRoles = new Set(
      (user?.UserPersonas || []).map((p: any) => p?.UserRole).filter(Boolean)
    );

    const admRole = [...SCORE_CARD_ADM_ROLES].some((r) => userRoles.has(r));
    return { admRole, scoreCardRole: admRole, pemRole: [...PEM_ROLES].some((r) => userRoles.has(r)) };
  } catch {
    return { admRole: false, scoreCardRole: false, pemRole: false };
  }
}

// -- Helpers
function getCurrentUser(): any {
  try {
    const str = sessionStorage.getItem('currentUser') || sessionStorage.getItem('loggedUser');
    if (!str) return null;
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed[0] : parsed;
  } catch {
    return null;
  }
}

function isMultiTin(): boolean {
  return sessionStorage.getItem('multiTinNavigation') === 'true';
}

function getMultiTins(): string | null {
  return sessionStorage.getItem('multiSelectedTins');
}

// Mirrors Angular sessionService.isExtendedSingleTin():
// multiTinNavigation=true AND exactly 1 TIN selected
function isExtendedSingleTin(): boolean {
  if (sessionStorage.getItem('multiTinNavigation') !== 'true') return false;
  const tins = sessionStorage.getItem('multiSelectedTins');
  return !!tins && tins.split(',').length === 1;
}

function is504TimeoutError(error: any): boolean {
  return error?.response?.status === 504 || error?.status === 504;
}

async function pushWith504Retry(url: string, payload: any): Promise<any> {
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
   return await menuRolesHttp.push(url, payload);
    } catch (error) {
      if (!is504TimeoutError(error) || attempt === 3) {
        throw error;
      }
    }
  }
}

// -- Async: pcorData
// Mirrors Angular pcor.service.ts getPCORMedicareData():
//   ProviderKey present + not extendedSingleTin → { ProviderSysKey } (internal regular)
//   extendedSingleTin or no ProviderKey         → { Tin: selectedTin.value } (external/extended)
//   multiTin                                     → { Tin: multiSelectedTins }
async function fetchPcorData(): Promise<boolean> {
  try {
    const user = getCurrentUser();
    const payload: Record<string, string> = {};

    if (isMultiTin()) {
      const tins = getMultiTins();
      if (!tins) return false;
      payload['Tin'] = tins;
    } else if (user?.ProviderKey && !isExtendedSingleTin()) {
      payload['ProviderSysKey'] = user.ProviderKey;
    } else if (user?.selectedTin?.value) {
      payload['Tin'] = user.selectedTin.value;
    } else {
      return false;
    }

    const data: any = await menuRolesHttp.push(
      `/${environment.apiUrls.PCORQualityMeasure}`,
      payload
    );
    const active = !!(data?.Data?.ReportingPeriod);
    sessionStorage.setItem('pcor', JSON.stringify({ Triggered: true, Active: active }));
    return active;
  } catch {
    return false;
  }
}

// -- Async: admData
// Handles both response shapes the ADM page supports:
//   { Data: { STATES: [...], LOBS: [...] } } and { Data: [ { EnI: [...], MnR: [...], YEARS: [...] } ] }
async function fetchAdmData(): Promise<boolean> {
  try {
    const providerSysKey = JSON.parse(sessionStorage.getItem('providerInfo') || '{}')?.ProviderSysKey;
    if (!providerSysKey) return false;

    const data: any = await menuRolesHttp.get(
      `/${environment.apiUrls.ADMStateLevel}${providerSysKey}`
    );

    // The ADM page only loads data when it has both a state and a LOB, so require both here.
    const dataObj = Array.isArray(data?.Data) ? data.Data[0] : data?.Data;
    const hasStructuredFilters =
      !!dataObj && (Array.isArray(dataObj.STATES) || Array.isArray(dataObj.LOBS));
    const active = hasStructuredFilters
      ? dataObj.STATES?.length > 0 && dataObj.LOBS?.length > 0
      // Each LOB-keyed bucket carries its own states, so a non-empty bucket satisfies both.
      : !!dataObj && Object.keys(dataObj).some(
        (key) => key !== 'YEARS' && Array.isArray(dataObj[key]) && dataObj[key].length > 0
      );

    sessionStorage.setItem('adm', JSON.stringify({ Triggered: true, Active: active }));
    return active;
  } catch {
    return false;
  }
}

// -- Async: affData
// Mirrors Angular hamburger-menu checkAffData():
//   ProviderKey present + not extendedSingleTin → ProviderSysKey
//   multi-TIN or external/extendedSingleTin     → Tin
async function fetchAffData(): Promise<boolean> {
  try {
    const user = getCurrentUser();
    const provTinListFilter: Record<string, any> = {};

    if (isMultiTin()) {
      const tins = getMultiTins();
      if (!tins) return false;
      provTinListFilter['Tin'] = tins;
    } else if (user?.ProviderKey && !isExtendedSingleTin()) {
      provTinListFilter['ProviderSysKey'] = user.ProviderKey;
    } else if (user?.selectedTin?.value) {
      provTinListFilter['Tin'] = user.selectedTin.value;
    } else {
      return false;
    }

    const payload = {
      RequestedJourney: 'provTinListFilter',
      ProvTinListFilter: provTinListFilter,
    };

    const data: any = await pushWith504Retry(
      `/${environment.apiUrls.TopFilterUrl}`,
      payload
    );
    const active = Array.isArray(data?.Data) && data.Data.length > 0;
    sessionStorage.setItem('aff', JSON.stringify({ Triggered: true, Active: active }));
    return active;
  } catch {
    return false;
  }
}

// -- Async: cesfData
// Mirrors Angular exemption-shared.service checkCESFData():
//   ProviderKey present + not extendedSingleTin → ProviderSysKey
//   multi-TIN or external/extendedSingleTin     → Tins
async function fetchCesfData(): Promise<boolean> {
  try {
    const user = getCurrentUser();
    const contractExemptionFilter: Record<string, any> = {};

    if (isMultiTin()) {
      const tins = getMultiTins();
      if (!tins) return false;
      contractExemptionFilter['Tins'] = tins;
    } else if (user?.ProviderKey && !isExtendedSingleTin()) {
      contractExemptionFilter['ProviderSysKey'] = user.ProviderKey;
    } else if (user?.selectedTin?.value) {
      contractExemptionFilter['Tins'] = user.selectedTin.value;
    } else {
      return false;
    }

    const payload = {
      RequestedJourney: 'contractExemptionSummaryFilter',
      ContractExemptionFilter: contractExemptionFilter,
    };

    // Angular uses 'ExtendedTinTopFilter' key -> 'extended-tins/top-filter' path
    const data: any = await pushWith504Retry('/extended-tins/top-filter', payload);
    const active = Array.isArray(data?.Data) && data.Data.length > 0;
    sessionStorage.setItem('cesf', JSON.stringify({ Triggered: true, Active: active }));
    return active;
  } catch {
    return false;
  }
}

/**
 * Hook that returns menu role flags.
 * Sync roles (from UserPersonas) are available immediately.
 * Async roles (pcorData, affData, cesfData) are fetched once on mount.
 * pocaEnabled is always false — VIM API call is suppressed in Angular.
 */
export function useMenuRoles(): MenuRoles {
  const syncRoles = deriveSyncRoles();

  const [roles, setRoles] = useState<MenuRoles>(() => ({
    ...DEFAULT_ROLES,
    ...syncRoles,
  }));

  // Run async API calls once on mount
  useEffect(() => {
    let cancelled = false;

    async function loadAsyncRoles() {
      if (sessionStorage.getItem('contractIdNavigation') === 'true') {
        return;
      }

      const [pcorData, affData, admData] = await Promise.all([
        fetchPcorData(),
        fetchAffData(),
        fetchAdmData(),
      ]);
      // cesfData only fetched if affData is false (mirrors Angular's checkCESFData guard)
      const cesfData = affData ? false : await fetchCesfData();

      if (cancelled) return;

      const updated: MenuRoles = {
        ...syncRoles,
        pcorData,
        admData,
        affData,
        cesfData,
        pocaEnabled: false,
      };

      sessionStorage.setItem('menuRoles', JSON.stringify(updated));
      setRoles(updated);
    }

    loadAsyncRoles();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep listener for MenuRolesUpdated — Angular may dispatch pocaEnabled
  // if VIM suppression is lifted in the future, or during hybrid mode
  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const incoming = (e as CustomEvent<Partial<MenuRoles>>).detail;
      if (incoming?.pocaEnabled) {
        setRoles((prev) => {
          const updated = { ...prev, pocaEnabled: true };
          sessionStorage.setItem('menuRoles', JSON.stringify(updated));
          return updated;
        });
      }
    };
    window.addEventListener('MenuRolesUpdated', handleUpdate);
    return () => window.removeEventListener('MenuRolesUpdated', handleUpdate);
  }, []);

  return roles;
}
